//projectlab-web/src/lib/logger.ts
type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const LEVEL_ORDER: Record<LogLevel, number> = {
  debug: 0,
  info:  1,
  warn:  2,
  error: 3,
};

const IS_DEBUG      = import.meta.env.VITE_DEBUG === 'true';
const CURRENT_LEVEL = (import.meta.env.VITE_LOG_LEVEL as LogLevel | undefined) ?? 'info';
const IS_DEV        = import.meta.env.DEV;

function shouldLog(level: LogLevel): boolean {
  if (!IS_DEBUG && level === 'debug') return false;
  return LEVEL_ORDER[level] >= LEVEL_ORDER[CURRENT_LEVEL];
}

function timestamp(): string {
  return new Date().toISOString().replace('T', ' ').replace('Z', '');
}

function formatTag(module: string, level: LogLevel): string {
  return `[PL:${module}][${level.toUpperCase()}] ${timestamp()}`;
}

// Colour codes for browser console (ignored in non-supporting envs)
const COLOURS: Record<LogLevel, string> = {
  debug: 'color:#7AA0FF;font-weight:600',
  info:  'color:#22C55E;font-weight:600',
  warn:  'color:#F59E0B;font-weight:600',
  error: 'color:#EF4444;font-weight:600',
};

export function createLogger(module: string) {
  return {
    debug(msg: string, data?: unknown): void {
      if (!shouldLog('debug')) return;
      console.debug(`%c${formatTag(module, 'debug')}`, COLOURS.debug, msg, data !== undefined ? data : '');
    },

    info(msg: string, data?: unknown): void {
      if (!shouldLog('info')) return;
      console.info(`%c${formatTag(module, 'info')}`, COLOURS.info, msg, data !== undefined ? data : '');
    },

    warn(msg: string, data?: unknown): void {
      if (!shouldLog('warn')) return;
      console.warn(`%c${formatTag(module, 'warn')}`, COLOURS.warn, msg, data !== undefined ? data : '');
    },

    error(msg: string, data?: unknown): void {
      if (!shouldLog('error')) return;
      console.error(`%c${formatTag(module, 'error')}`, COLOURS.error, msg, data !== undefined ? data : '');
    },

    /** Group related log entries — use sparingly */
    group(label: string, fn: () => void): void {
      if (!IS_DEBUG) { fn(); return; }
      console.groupCollapsed(`[PL:${module}] ${label}`);
      fn();
      console.groupEnd();
    },

    /** Time a block of async code */
    async time<T>(label: string, fn: () => Promise<T>): Promise<T> {
      if (!IS_DEBUG) return fn();
      const start = performance.now();
      try {
        const result = await fn();
        const ms = (performance.now() - start).toFixed(1);
        console.debug(`%c${formatTag(module, 'debug')}`, COLOURS.debug, `⏱ ${label} completed in ${ms}ms`);
        return result;
      } catch (err) {
        const ms = (performance.now() - start).toFixed(1);
        console.error(`%c${formatTag(module, 'error')}`, COLOURS.error, `⏱ ${label} failed after ${ms}ms`, err);
        throw err;
      }
    },
  };
}

// ============================================================
// Global error capture (dev only)
// ============================================================
if (IS_DEBUG && IS_DEV) {
  const globalLog = createLogger('GlobalErrorHandler');

  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    globalLog.error('Unhandled promise rejection', {
      reason:  e.reason,
      message: e.reason instanceof Error ? e.reason.message : String(e.reason),
      stack:   e.reason instanceof Error ? e.reason.stack   : undefined,
    });
  });

  window.addEventListener('error', (e: ErrorEvent) => {
    globalLog.error('Uncaught error', {
      message:  e.message,
      filename: e.filename,
      line:     e.lineno,
      col:      e.colno,
    });
  });

  // Log environment on startup
  createLogger('Env').debug('Environment loaded', {
    mode:      import.meta.env.MODE,
    debug:     IS_DEBUG,
    logLevel:  CURRENT_LEVEL,
    supabaseUrl: import.meta.env.VITE_SUPABASE_URL ? '✓ set' : '✗ MISSING',
    anonKey:     import.meta.env.VITE_SUPABASE_ANON_KEY ? '✓ set' : '✗ MISSING',
  });
}
